import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import WellData from '../components/WellData';
import WellPath2D from '../components/WellPath2D';
import WellPath3D from '../components/WellPath3D';
import './Deviation_v2.css';

const Deviation_v2 = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [wellId, setWellId] = useState(null);

    // Get wellId from URL parameters on component mount
    useEffect(() => {
        const urlWellId = searchParams.get('wellId');
        if (urlWellId) {
            setWellId(urlWellId);
        }
    }, [searchParams]);

    // Additional state for deviation-specific features
    const [surveyPlot, setSurveyPlot] = useState(null);
    const [showPlotModal, setShowPlotModal] = useState(false);
    const [plotType, setPlotType] = useState('plan'); // plan, vertical, 3d

    // Configuration for the WellData component
    const wellDataConfig = {
        tables: { parent: 'SurveyHeader', child: 'SurveyPoint' },
        identityColumns: { parent: 'SurveyID', child: 'PointID' },
        matchKey: { parentKey: 'SurveyID', childForeignKey: 'SurveyID' },
        title: 'Well Deviation',
        icon: '📏',
        parentIcon: '📋',
        childIcon: '📍',
        emptyParentMessage: 'No survey headers found for this well',
        emptyChildMessage: 'No survey points found for this survey',
        // Display configuration
        showTabs: true,
        tabs: [
            { key: 'details', label: 'Survey Points', icon: '📍' },
            { key: 'plot', label: 'Well Path Plot', icon: '📊' },
        ],
        // Custom field mappings
        parentDisplayFields: ['SurveyName', 'SurveyDate', 'SurveyType', 'TotalDepth', 'MaxInclination'],
        // Column filters
        hiddenParentFields: ['WellID'],
        hiddenChildFields: ['SurveyID'],
        // Additional features
        enableInlineEdit: true,
        enableImageUpload: false,
        enableAttachments: false
    };

    // Map survey points data to format expected by plotting components
    const mapSurveyDataForPlotting = useCallback((surveyPoints) => {
        if (!surveyPoints || surveyPoints.length === 0) return [];
        
        // Debug logging to see actual field names and values
        if (surveyPoints.length > 0) {
            console.log('🔍 Survey Points Debug Info:');
            console.log('First survey point fields:', Object.keys(surveyPoints[0]));
            console.log('First survey point data:', surveyPoints[0]);
            
            // Check ALL possible field names for DX/DY
            const firstPoint = surveyPoints[0];
            const allFields = Object.keys(firstPoint);
            
            console.log('🔍 All field names:', allFields);
            console.log('🔍 All field values:', firstPoint);
            
            // Look for any field that might contain DX/Easting data
            const possibleDxFields = allFields.filter(field => 
                field.toLowerCase().includes('dx') || 
                field.toLowerCase().includes('east') || 
                field.toLowerCase().includes('x') ||
                field.includes('X')
            );
            
            // Look for any field that might contain DY/Northing data  
            const possibleDyFields = allFields.filter(field => 
                field.toLowerCase().includes('dy') || 
                field.toLowerCase().includes('north') || 
                field.toLowerCase().includes('y') ||
                field.includes('Y')
            );
            
            console.log('🔍 Possible DX fields:', possibleDxFields.map(field => ({
                field,
                value: firstPoint[field]
            })));
            
            console.log('🔍 Possible DY fields:', possibleDyFields.map(field => ({
                field,
                value: firstPoint[field]
            })));
            
            // Check specific common field names
            console.log('🔍 Specific field checks:', {
                'Dx': firstPoint.Dx,
                'DX': firstPoint.DX,
                'dx': firstPoint.dx,
                'Easting': firstPoint.Easting,
                'easting': firstPoint.easting,
                'X': firstPoint.X,
                'x': firstPoint.x,
                'Dy': firstPoint.Dy,
                'DY': firstPoint.DY,
                'dy': firstPoint.dy,
                'Northing': firstPoint.Northing,
                'northing': firstPoint.northing,
                'Y': firstPoint.Y,
                'y': firstPoint.y
            });
        }
        
        return surveyPoints.map(point => {
            // Based on database schema: MD, Dx, Dy, TVD, Inclination, Azimuth
            const getDxValue = (point) => {
                const dxRaw = point.Dx;
                const dxParsed = parseFloat(dxRaw);
                
                // Check for null, undefined, or NaN
                if (dxRaw === null || dxRaw === undefined || isNaN(dxParsed)) {
                    return 0;
                }
                return dxParsed;
            };
            
            const getDyValue = (point) => {
                const dyRaw = point.Dy;
                const dyParsed = parseFloat(dyRaw);
                
                // Check for null, undefined, or NaN
                if (dyRaw === null || dyRaw === undefined || isNaN(dyParsed)) {
                    return 0;
                }
                return dyParsed;
            };

            const mappedPoint = {
                // Map field names based on exact database schema
                Depth: parseFloat(point.MD) || 0,
                DX: getDxValue(point),
                DY: getDyValue(point),
                TVD: parseFloat(point.TVD) || 0,
                Inclination: parseFloat(point.Inclination) || 0,
                Azimuth: parseFloat(point.Azimuth) || 0,
                // Keep original fields for reference
                ...point
            };
            
            // Debug the mapping for first few points
            if (surveyPoints.indexOf(point) < 3) {
                console.log(`📊 Mapped point ${surveyPoints.indexOf(point) + 1}:`, {
                    rawDx: point.Dx,
                    rawDy: point.Dy,
                    rawDxType: typeof point.Dx,
                    rawDyType: typeof point.Dy,
                    isDxNull: point.Dx === null,
                    isDyNull: point.Dy === null,
                    isDxUndefined: point.Dx === undefined,
                    isDyUndefined: point.Dy === undefined,
                    mappedDX: mappedPoint.DX,
                    mappedDY: mappedPoint.DY,
                    allRawData: {
                        MD: point.MD,
                        Dx: point.Dx,
                        Dy: point.Dy,
                        TVD: point.TVD,
                        Inclination: point.Inclination,
                        Azimuth: point.Azimuth
                    }
                });
            }
            
            return mappedPoint;
        }).sort((a, b) => a.Depth - b.Depth);
    }, []);

    // Handle parent (Survey Header) selection
    const handleSurveySelect = useCallback(async (survey) => {
        console.log('📐 Survey selected:', survey);
        
        // Load survey plot data if needed
        if (survey && survey.SurveyID) {
            try {
                console.log('📊 Would load survey plot data for:', survey.SurveyID);
                // In a real implementation, you might load plot data here
                // const plotData = await apiFetch(`/api/surveys/${survey.SurveyID}/plot-data`);
                // setSurveyPlot(plotData);
            } catch (err) {
                console.error('Failed to load survey plot data:', err);
                setSurveyPlot(null);
            }
        }
    }, []);

    // Custom tab content renderer
    const renderCustomTabContent = useCallback((activeTab, selectedParent, childData, wellId) => {
        switch (activeTab) {
            case 'plot':
                return renderWellPathPlot(selectedParent, childData);
            case 'summary':
                return renderSurveySummary(selectedParent, childData);
            default:
                return null; // Let WellData handle the default 'details' tab
        }
    }, []);

    // Render well path plot tab
    const renderWellPathPlot = (selectedSurvey, surveyPoints) => {
        if (!selectedSurvey) {
            return (
                <div className="plot-placeholder">
                    <div className="placeholder-icon">📊</div>
                    <p>Select a survey to view well path plot</p>
                </div>
            );
        }

        // Map data for plotting components
        const plottingData = mapSurveyDataForPlotting(surveyPoints);

        return (
            <div className="well-path-plot-section">
                <div className="section-header">
                    <h3>Well Path Visualization</h3>
                    <div className="plot-controls">
                        <select 
                            value={plotType} 
                            onChange={(e) => setPlotType(e.target.value)}
                            className="plot-type-select"
                        >
                            <option value="plan">2D Plan View</option>
                            <option value="vertical">2D Vertical Section</option>
                            <option value="3d">3D View</option>
                        </select>
                        <button 
                            className="fullscreen-plot-btn"
                            onClick={() => setShowPlotModal(true)}
                            disabled={!surveyPoints || surveyPoints.length === 0}
                        >
                            🔍 Full Screen
                        </button>
                    </div>
                </div>

                <div className="plot-container">
                    {plottingData && plottingData.length > 0 ? (
                        <div className="plot-content">
                            <div className="plot-info">
                                <h4>{plotType === '3d' ? '3D View' : `2D ${plotType.charAt(0).toUpperCase() + plotType.slice(1)} View`}</h4>
                                <p>{plottingData.length} survey points</p>
                                <p>Survey: {selectedSurvey.SurveyName || 'Unnamed'}</p>
                            </div>
                            
                            {/* Render actual plotting components */}
                            <div className="plot-visualization">
                                {plotType === '3d' ? (
                                    <WellPath3D data={plottingData} />
                                ) : (
                                    (() => {
                                        // Debug the data being passed to WellPath2D
                                        console.log('🎯 Data being passed to WellPath2D:', plottingData);
                                        if (plottingData && plottingData.length > 0) {
                                            console.log('🎯 First 3 points for WellPath2D:', plottingData.slice(0, 3).map(p => ({
                                                Depth: p.Depth,
                                                DX: p.DX,
                                                DY: p.DY,
                                                TVD: p.TVD,
                                                Inclination: p.Inclination,
                                                Azimuth: p.Azimuth
                                            })));
                                        }
                                        return <WellPath2D data={plottingData} />;
                                    })()
                                )}
                            </div>
                            
                            <div className="plot-stats">
                                <div className="stat-item">
                                    <span className="stat-label">Max Depth:</span>
                                    <span className="stat-value">
                                        {Math.max(...plottingData.map(p => p.Depth)).toFixed(2)} m
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Max Inclination:</span>
                                    <span className="stat-value">
                                        {Math.max(...plottingData.map(p => p.Inclination)).toFixed(2)}°
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Max TVD:</span>
                                    <span className="stat-value">
                                        {Math.max(...plottingData.map(p => p.TVD)).toFixed(2)} m
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="plot-empty">
                            <div className="empty-icon">📊</div>
                            <p>No survey points to plot</p>
                            <p>Add survey points to see the well path visualization</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render survey summary tab
    const renderSurveySummary = (selectedSurvey, surveyPoints) => {
        if (!selectedSurvey) {
            return (
                <div className="summary-placeholder">
                    <div className="placeholder-icon">📈</div>
                    <p>Select a survey to view summary</p>
                </div>
            );
        }

        // Calculate survey statistics
        const stats = calculateSurveyStats(surveyPoints || []);

        return (
            <div className="survey-summary-section">
                <div className="section-header">
                    <h3>Survey Summary</h3>
                    <p>Statistical analysis of survey data</p>
                </div>

                <div className="summary-grid">
                    <div className="summary-card">
                        <div className="card-header">
                            <h4>📍 Survey Points</h4>
                        </div>
                        <div className="card-content">
                            <div className="summary-stat">
                                <span className="stat-label">Total Points:</span>
                                <span className="stat-value">{stats.totalPoints}</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Depth Range:</span>
                                <span className="stat-value">
                                    {stats.minDepth.toFixed(2)} - {stats.maxDepth.toFixed(2)} m
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-header">
                            <h4>📐 Inclination</h4>
                        </div>
                        <div className="card-content">
                            <div className="summary-stat">
                                <span className="stat-label">Maximum:</span>
                                <span className="stat-value">{stats.maxInclination.toFixed(2)}°</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Average:</span>
                                <span className="stat-value">{stats.avgInclination.toFixed(2)}°</span>
                            </div>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-header">
                            <h4>🧭 Azimuth</h4>
                        </div>
                        <div className="card-content">
                            <div className="summary-stat">
                                <span className="stat-label">Range:</span>
                                <span className="stat-value">
                                    {stats.minAzimuth.toFixed(1)}° - {stats.maxAzimuth.toFixed(1)}°
                                </span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Average:</span>
                                <span className="stat-value">{stats.avgAzimuth.toFixed(1)}°</span>
                            </div>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-header">
                            <h4>📏 Displacement</h4>
                        </div>
                        <div className="card-content">
                            <div className="summary-stat">
                                <span className="stat-label">Horizontal:</span>
                                <span className="stat-value">{stats.maxHorizontalDisplacement.toFixed(2)} m</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">TVD:</span>
                                <span className="stat-value">{stats.maxTVD.toFixed(2)} m</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="survey-info-section">
                    <h4>Survey Information</h4>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Survey Name:</span>
                            <span className="info-value">{selectedSurvey.SurveyName || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Survey Date:</span>
                            <span className="info-value">
                                {selectedSurvey.SurveyDate ? new Date(selectedSurvey.SurveyDate).toLocaleDateString() : 'N/A'}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Survey Type:</span>
                            <span className="info-value">{selectedSurvey.SurveyType || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Surveyor:</span>
                            <span className="info-value">{selectedSurvey.Surveyor || 'N/A'}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Calculate survey statistics
    const calculateSurveyStats = (surveyPoints) => {
        if (!surveyPoints || surveyPoints.length === 0) {
            return {
                totalPoints: 0,
                minDepth: 0,
                maxDepth: 0,
                maxInclination: 0,
                avgInclination: 0,
                minAzimuth: 0,
                maxAzimuth: 0,
                avgAzimuth: 0,
                maxHorizontalDisplacement: 0,
                maxTVD: 0
            };
        }

        // Use correct field names: MD, Dx, Dy, TVD, Inclination, Azimuth
        const depths = surveyPoints.map(p => parseFloat(p.MD) || parseFloat(p.MeasuredDepth) || 0);
        const inclinations = surveyPoints.map(p => parseFloat(p.Inclination) || 0);
        const azimuths = surveyPoints.map(p => parseFloat(p.Azimuth) || 0);
        const tvds = surveyPoints.map(p => parseFloat(p.TVD) || 0);
        const dx_values = surveyPoints.map(p => parseFloat(p.Dx) || parseFloat(p.DX) || parseFloat(p.Easting) || 0);
        const dy_values = surveyPoints.map(p => parseFloat(p.Dy) || parseFloat(p.DY) || parseFloat(p.Northing) || 0);

        const horizontalDisplacements = surveyPoints.map(p => {
            const dx = parseFloat(p.Dx) || parseFloat(p.DX) || parseFloat(p.Easting) || 0;
            const dy = parseFloat(p.Dy) || parseFloat(p.DY) || parseFloat(p.Northing) || 0;
            return Math.sqrt(dx * dx + dy * dy);
        });

        return {
            totalPoints: surveyPoints.length,
            minDepth: depths.length > 0 ? Math.min(...depths) : 0,
            maxDepth: depths.length > 0 ? Math.max(...depths) : 0,
            maxInclination: inclinations.length > 0 ? Math.max(...inclinations) : 0,
            avgInclination: inclinations.length > 0 ? inclinations.reduce((a, b) => a + b, 0) / inclinations.length : 0,
            minAzimuth: azimuths.length > 0 ? Math.min(...azimuths) : 0,
            maxAzimuth: azimuths.length > 0 ? Math.max(...azimuths) : 0,
            avgAzimuth: azimuths.length > 0 ? azimuths.reduce((a, b) => a + b, 0) / azimuths.length : 0,
            maxHorizontalDisplacement: horizontalDisplacements.length > 0 ? Math.max(...horizontalDisplacements) : 0,
            maxTVD: tvds.length > 0 ? Math.max(...tvds) : 0
        };
    };

    // Event handlers for WellData component
    const handleParentSave = useCallback((result) => {
        console.log('📐 Survey saved:', result);
        // Add any survey-specific post-save logic here
    }, []);

    const handleChildSave = useCallback((result) => {
        console.log('📍 Survey point saved:', result);
        // Add any survey point-specific post-save logic here
    }, []);

    const handleWellChange = useCallback((wellId) => {
        setSearchParams({ wellId });
    }, [setSearchParams]);

    return (
        <div className="deviation-v2-container">
            <WellData
                config={wellDataConfig}
                wellId={wellId}
                onWellChange={handleWellChange}
                onParentSelect={handleSurveySelect}
                onParentSave={handleParentSave}
                onChildSave={handleChildSave}
                renderCustomTabContent={renderCustomTabContent}
            />

            {/* Plot Modal */}
            {showPlotModal && (
                <div className="modal-overlay plot-modal" onClick={() => setShowPlotModal(false)}>
                    <div className="plot-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="plot-modal-header">
                            <h3>Well Path Plot - {plotType === '3d' ? '3D View' : `2D ${plotType.charAt(0).toUpperCase() + plotType.slice(1)} View`}</h3>
                            <div className="plot-modal-controls">
                                <select 
                                    value={plotType} 
                                    onChange={(e) => setPlotType(e.target.value)}
                                    className="plot-type-select"
                                >
                                    <option value="plan">2D Plan View</option>
                                    <option value="vertical">2D Vertical Section</option>
                                    <option value="3d">3D View</option>
                                </select>
                                <button 
                                    className="close-btn"
                                    onClick={() => setShowPlotModal(false)}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        <div className="plot-modal-body">
                            {/* Get the current survey data for the modal */}
                            {(() => {
                                // This is a bit of a hack to get the current data in the modal
                                // In a real implementation, you'd pass this data through state
                                const currentWellData = document.querySelector('.welldata-container');
                                if (currentWellData) {
                                    // For now, we'll just show a message that the plot would be here
                                    // In practice, you'd manage this data through proper state management
                                }
                                return (
                                    <div className="fullscreen-plot-container">
                                        <div className="plot-info">
                                            <h4>Full Screen Interactive Plot</h4>
                                            <p>Interactive {plotType === '3d' ? '3D' : '2D'} visualization with zoom, pan, and measurement capabilities</p>
                                        </div>
                                        {/* Note: For full implementation, you'd pass the actual survey data here */}
                                        <div className="plot-placeholder-message">
                                            <div className="plot-icon">📊</div>
                                            <p>Full screen plot would render here with survey data</p>
                                            <p className="note">To fully implement: Pass survey data to this modal</p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Deviation_v2; 